import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const NotificationAnalytics = ({ analytics, notifications }) => {
  // Process type breakdown data for pie chart
  const typeBreakdownData = analytics?.typeBreakdown ? 
    Object.entries(analytics?.typeBreakdown)?.map(([type, count]) => ({
      name: type?.charAt(0)?.toUpperCase() + type?.slice(1),
      value: count,
      color: getTypeColor(type)
    })) : [];

  // Process recent activity for timeline
  const recentActivity = analytics?.recentActivity?.slice(0, 5) || [];

  // Colors for different notification types
  function getTypeColor(type) {
    const colors = {
      general: '#3b82f6',
      event: '#10b981',
      release: '#f59e0b',
      merchandise: '#8b5cf6'
    };
    return colors?.[type] || '#6b7280';
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0];
      return (
        <div className="bg-surface p-3 rounded-lg border border-border shadow-lg">
          <p className="font-medium text-text-primary">{data?.name}</p>
          <p className="text-sm text-text-secondary">
            <span className="font-medium" style={{ color: data?.payload?.color }}>
              {data?.value}
            </span> notifications
          </p>
        </div>
      );
    }
    return null;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      draft: 'Edit3',
      scheduled: 'Clock',
      sent: 'CheckCircle',
      failed: 'XCircle'
    };
    return icons?.[status] || 'MessageCircle';
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      draft: 'text-gray-600',
      scheduled: 'text-blue-600',
      sent: 'text-green-600',
      failed: 'text-red-600'
    };
    return colors?.[status] || 'text-gray-600';
  };

  if (!analytics) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="BarChart3" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No analytics data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text-primary mb-2">Notification Analytics</h2>
        <p className="text-sm text-text-secondary">
          Performance metrics and insights for your notifications
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-blue-900/40 rounded-lg flex items-center justify-center">
              <Icon name="Send" size={16} className="text-blue-400" />
            </div>
            <div className="text-xs text-text-secondary">Total</div>
          </div>
          <div className="text-2xl font-bold text-text-primary mb-1">
            {analytics?.totalSent || 0}
          </div>
          <div className="text-sm text-text-secondary">Notifications Sent</div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-green-900/40 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={16} className="text-green-400" />
            </div>
            <div className="text-xs text-text-secondary">Success</div>
          </div>
          <div className="text-2xl font-bold text-text-primary mb-1">
            {analytics?.totalDelivered || 0}
          </div>
          <div className="text-sm text-text-secondary">Successfully Delivered</div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-red-900/40 rounded-lg flex items-center justify-center">
              <Icon name="XCircle" size={16} className="text-red-400" />
            </div>
            <div className="text-xs text-text-secondary">Failed</div>
          </div>
          <div className="text-2xl font-bold text-text-primary mb-1">
            {analytics?.totalFailed || 0}
          </div>
          <div className="text-sm text-text-secondary">Delivery Failures</div>
        </div>

        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-orange-900/40 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={16} className="text-orange-400" />
            </div>
            <div className="text-xs text-text-secondary">Rate</div>
          </div>
          <div className="text-2xl font-bold text-text-primary mb-1">
            {analytics?.deliveryRate || 0}%
          </div>
          <div className="text-sm text-text-secondary">Delivery Success Rate</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Notification Types Breakdown */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Notification Types</h3>
          
          {typeBreakdownData?.length > 0 ? (
            <>
              <div className="h-48 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={typeBreakdownData}
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {typeBreakdownData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend */}
              <div className="space-y-2">
                {typeBreakdownData?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item?.color }}
                      />
                      <span className="text-sm text-text-secondary">{item?.name}</span>
                    </div>
                    <span className="text-sm font-medium text-text-primary">{item?.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Icon name="PieChart" size={32} className="text-text-secondary mx-auto mb-2" />
              <p className="text-sm text-text-secondary">No type data available</p>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Activity</h3>
          
          {recentActivity?.length > 0 ? (
            <div className="space-y-4">
              {recentActivity?.map((activity, index) => (
                <div key={activity?.id || index} className="flex items-start space-x-3 p-3 bg-background/50 rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity?.status === 'sent' ? 'bg-green-100' :
                    activity?.status === 'scheduled' ? 'bg-blue-100' :
                    activity?.status === 'failed'? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    <Icon 
                      name={getStatusIcon(activity?.status)} 
                      size={14} 
                      className={getStatusColor(activity?.status)}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {activity?.title}
                      </p>
                      <span className="text-xs text-text-secondary ml-2">
                        {formatDate(activity?.sentAt || activity?.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-text-secondary capitalize">
                        {activity?.type?.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-text-secondary">•</span>
                      <span className="text-xs text-text-secondary capitalize">
                        {activity?.status}
                      </span>
                      {activity?.deliveryCount > 0 && (
                        <>
                          <span className="text-xs text-text-secondary">•</span>
                          <span className="text-xs text-text-secondary">
                            {activity?.deliveryCount} delivered
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="Activity" size={32} className="text-text-secondary mx-auto mb-2" />
              <p className="text-sm text-text-secondary">No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Performance Insights */}
      <div className="mt-8 bg-blue-950/50 border border-blue-900/40 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={20} className="text-blue-400 mt-0.5" />
          <div>
            <h4 className="text-lg font-medium text-blue-300 mb-2">Performance Insights</h4>
            <div className="space-y-2 text-sm text-blue-200">
              {analytics?.deliveryRate >= 90 && (
                <p>🎉 Excellent delivery rate! Your notifications are reaching users effectively.</p>
              )}
              {analytics?.deliveryRate < 70 && (
                <p>⚠️ Consider reviewing your audience targeting to improve delivery rates.</p>
              )}
              {analytics?.totalSent === 0 && (
                <p>📝 Start by sending your first notification to engage with your audience.</p>
              )}
              {analytics?.totalSent > 0 && analytics?.totalSent < 5 && (
                <p>🚀 Great start! Consider sending regular updates to build engagement.</p>
              )}
              {typeBreakdownData?.length > 1 && (
                <p>📊 You're using multiple notification types - great for varied engagement!</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationAnalytics;